import PasswordField from '@/components/form/PasswordField';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Form } from '@/components/primitives/Form';
import { changePasswordLoadingSelector } from '@/features/loading/loadingSelectors';
import { changePassword } from '@/features/reset-password/resetPasswordActions';
import { usePageTitle } from '@/hooks/usePageTitle';
import usePasswordComplexity from '@/hooks/usePasswordComplexity';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import NewPasswordFormSkeleton from '@/routes/reset-password/NewPasswordFormSkeleton';
import { useDispatch, useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import {
  createChangePasswordSchema,
  type ChangePasswordValues,
} from '@/utils/zodSchema';
import { Button, Card, Divider, Icons, Typography } from '@fleet/webshop-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, type FC } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const SETTINGS_PATH = '/account/details/settings';

const ChangePassword: FC = () => {
  const loading = useSelector(changePasswordLoadingSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });
  const { passwordComplexity, loading: passwordRequirementsLoading } =
    usePasswordComplexity();
  const { t } = useTranslation();

  usePageTitle('changePassword');

  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(
      createChangePasswordSchema(t, passwordComplexity?.regex)
    ),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      repeatNewPassword: '',
    },
  });
  const {
    trigger,
    formState: { submitCount },
  } = form;

  const goBack = useCallback(() => navigate(SETTINGS_PATH), [navigate]);

  const onSubmit = useCallback(
    async ({
      currentPassword,
      newPassword,
      repeatNewPassword,
    }: ChangePasswordValues) => {
      try {
        await dispatch(
          changePassword({
            currentPassword,
            newPassword,
            confirmNewPassword: repeatNewPassword,
          })
        ).unwrap();

        toast.success(<TransAlert i18nKey="passwordSuccessfullyChanged" />);
        navigate(SETTINGS_PATH);
      } catch {
        toast.error(<TransAlert i18nKey="passwordChangeFailed" />);
      }
    },
    [dispatch, navigate]
  );

  const validatePasswordFields = useCallback(
    () => submitCount > 0 && trigger(['newPassword', 'repeatNewPassword']),
    [submitCount, trigger]
  );

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full max-w-[410px] flex-col gap-6 p-4 tablet:my-auto laptop:p-0">
        <div className="flex items-center gap-2">
          <Button
            data-testid="change-password-back-button"
            variant="icon"
            aria-label={t('text.goBack')}
            onClick={goBack}
          >
            <Icons.arrowLeft />
          </Button>
          <Typography
            variant={isLaptopOrBigger ? 'title' : 'mobile-title'}
            as="h1"
          >
            <TransText i18nKey="changePassword" />
          </Typography>
        </div>

        {passwordRequirementsLoading ? (
          <NewPasswordFormSkeleton />
        ) : (
          <Card className="flex flex-col gap-4 rounded-xl">
            <Typography variant={isLaptopOrBigger ? 'heading2' : 'subtitle'}>
              <TransText i18nKey="changePassword" />
            </Typography>
            {passwordComplexity?.regexLocalization?.description && (
              <Typography variant="body1">
                {passwordComplexity.regexLocalization.description}
              </Typography>
            )}
            <Divider />
            <Form
              form={form}
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-3"
            >
              <PasswordField
                label={<TransText i18nKey="insertCurrentPassword" />}
                name="currentPassword"
                required
              />
              <PasswordField
                label={<TransText i18nKey="createNewPassword" />}
                name="newPassword"
                onBlur={validatePasswordFields}
                required
              />
              <PasswordField
                label={<TransText i18nKey="repeatNewPassword" />}
                name="repeatNewPassword"
                onBlur={validatePasswordFields}
                required
              />
              <div className="mt-2 flex w-full gap-4">
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={goBack}
                  data-testid="change-password-cancel-button"
                >
                  <TransText i18nKey="back" />
                </Button>
                <Button
                  data-testid="change-password-confirm-button"
                  type="submit"
                  variant="secondary"
                  className="gap-2"
                  startAdornment={<Icons.check className="h-5 w-5" />}
                  isPending={loading}
                >
                  <TransText i18nKey="confirm" />
                </Button>
              </div>
            </Form>
          </Card>
        )}
      </div>
    </CenteredLayout>
  );
};

export default ChangePassword;
